import { getIsoConstructionName } from "./construction";
import { RawAddressWithIndices } from "./types";
import {
  KM_TO_MILES,
  INTEGRATION_NOT_RUN,
  INTEGRATION_FOREIGN,
  INTEGRATION_ZERO_TIV,
  INTEGRATION_NO_MATCH,
} from "./utils";

export type GroupableField = {
  key: string;
  label: string;
  group: string;
  getValue: (addr: RawAddressWithIndices) => string | undefined | null;
  compare?: (a: string, b: string) => number;
};

// ---------------------------------------------------------------------------
// Bucketing helpers
// ---------------------------------------------------------------------------

const DIST_TO_COAST_BINS: { lo: number; hi: number; label: string }[] = [
  { lo: 0, hi: 0.1, label: "0 - 0.1 mi" },
  { lo: 0.1, hi: 0.25, label: "0.1 - 0.25 mi" },
  { lo: 0.25, hi: 0.75, label: "0.25 - 0.75 mi" },
  { lo: 0.75, hi: 1.5, label: "0.75 - 1.5 mi" },
  { lo: 1.5, hi: 2, label: "1.5 - 2 mi" },
  { lo: 2, hi: 5, label: "2 - 5 mi" },
  { lo: 5, hi: 10, label: "5 - 10 mi" },
  { lo: 10, hi: 25, label: "10 - 25 mi" },
  { lo: 25, hi: 50, label: "25 - 50 mi" },
  { lo: 50, hi: Infinity, label: "> 50 mi" },
];

function bucketYearBuilt(
  yearStr: string | null | undefined
): string | undefined {
  if (!yearStr) return undefined;
  const year = parseInt(yearStr, 10);
  if (isNaN(year)) return undefined;
  if (year < 1960) return "Pre-1960s";
  return `${Math.floor(year / 10) * 10}s`;
}

function bucketStories(
  storiesStr: string | null | undefined
): string | undefined {
  if (!storiesStr) return undefined;
  const n = parseInt(storiesStr, 10);
  if (isNaN(n)) return undefined;
  if (n <= 1) return "1";
  if (n <= 3) return "2-3";
  if (n <= 10) return "4-10";
  if (n <= 20) return "11-20";
  return "20+";
}

function bucketDistToCoast(addr: RawAddressWithIndices): string | undefined {
  const rms = addr.__extras.integrationData?.rms;
  if (!rms) return INTEGRATION_NOT_RUN;
  if (rms.is_foreign_exposure) return INTEGRATION_FOREIGN;
  if (addr.tiv === 0) return INTEGRATION_ZERO_TIV;

  const distKm = rms.location_detail?.locationWsDetail?.distToCoast;
  if (distKm == null) return INTEGRATION_NO_MATCH;

  const distMi = distKm * KM_TO_MILES;
  for (const bin of DIST_TO_COAST_BINS) {
    if (bin.hi === Infinity) {
      if (distMi >= bin.lo) return bin.label;
    } else if (distMi >= bin.lo && distMi < bin.hi) {
      return bin.label;
    }
  }
  return undefined;
}

function getFloodZone(addr: RawAddressWithIndices): string | undefined {
  const geo = addr.__extras.integrationData?.geographic_enrichment;
  if (geo == null) return INTEGRATION_NOT_RUN;
  return geo.flood_zone ?? "None";
}

function getEarthquakeZone(addr: RawAddressWithIndices): string | undefined {
  const geo = addr.__extras.integrationData?.geographic_enrichment;
  if (geo == null) return INTEGRATION_NOT_RUN;
  return geo.earthquake_zone ?? "None";
}

function getIsoConstruction(addr: RawAddressWithIndices): string | undefined {
  const name = getIsoConstructionName(
    addr.construction_code_type,
    addr.construction_code
  );
  return name ?? undefined;
}

// ---------------------------------------------------------------------------
// Comparators
// ---------------------------------------------------------------------------

const compareAlpha = (a: string, b: string) => a.localeCompare(b);

const compareDecadeBucket = (a: string, b: string) => {
  const aKey = a === "Pre-1960s" ? "0" : a;
  const bKey = b === "Pre-1960s" ? "0" : b;
  return aKey.localeCompare(bKey);
};

function compareBinOrder(labels: string[]): (a: string, b: string) => number {
  const order = new Map(labels.map((label, i) => [label, i]));
  return (a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999);
}

const compareDistToCoast = compareBinOrder(
  DIST_TO_COAST_BINS.map((b) => b.label)
);

// ---------------------------------------------------------------------------
// Groupable fields
// ---------------------------------------------------------------------------

export const GROUPABLE_FIELDS: GroupableField[] = [
  // Location
  {
    key: "state",
    label: "State",
    group: "Location",
    getValue: (addr) => addr.state,
  },
  {
    key: "county",
    label: "County",
    group: "Location",
    getValue: (addr) => addr.county,
  },
  {
    key: "city",
    label: "City",
    group: "Location",
    getValue: (addr) => addr.city,
  },
  {
    key: "zip",
    label: "Zip",
    group: "Location",
    getValue: (addr) => addr.zip,
  },
  {
    key: "country",
    label: "Country",
    group: "Location",
    getValue: (addr) => addr.country,
  },

  // Building
  {
    key: "year_built",
    label: "Year Built (Decade)",
    group: "Building",
    getValue: (addr) => bucketYearBuilt(addr.year_built),
    compare: compareDecadeBucket,
  },
  {
    key: "number_of_stories",
    label: "Stories",
    group: "Building",
    getValue: (addr) => bucketStories(addr.number_of_stories),
  },
  {
    key: "construction",
    label: "Construction Type (ISO)",
    group: "Building",
    getValue: (addr) => getIsoConstruction(addr),
  },
  {
    key: "occupancy_description",
    label: "Occupancy",
    group: "Building",
    getValue: (addr) => addr.occupancy_description,
  },
  {
    key: "number_of_buildings",
    label: "# of Buildings",
    group: "Building",
    getValue: (addr) => addr.number_of_buildings,
  },

  // Risk
  {
    key: "sic_code",
    label: "SIC Code",
    group: "Risk",
    getValue: (addr) => addr.sic_code,
  },
  {
    key: "sprinkler",
    label: "Sprinkler",
    group: "Risk",
    getValue: (addr) => addr.sprinkler,
  },
  {
    key: "sprinkler_type",
    label: "Sprinkler Type",
    group: "Risk",
    getValue: (addr) => addr.sprinkler_type,
  },
  {
    key: "flood_zone_broker",
    label: "Flood Zone (Broker)",
    group: "Risk",
    getValue: (addr) => addr.flood_zone,
  },
  {
    key: "roof_covering",
    label: "Roof Covering",
    group: "Risk",
    getValue: (addr) => addr.roof_covering,
  },
  {
    key: "ws_cladding_type",
    label: "WS Cladding",
    group: "Risk",
    getValue: (addr) => addr.ws_cladding_type,
  },
  {
    key: "fire_alarm_type",
    label: "Fire Alarm",
    group: "Risk",
    getValue: (addr) => addr.fire_alarm_type,
  },
  {
    key: "burglar_alarm_type",
    label: "Burglar Alarm",
    group: "Risk",
    getValue: (addr) => addr.burglar_alarm_type,
  },

  {
    key: "flood_zone_fema",
    label: "Flood Zone (FEMA)",
    group: "Geo",
    getValue: (addr) => getFloodZone(addr),
  },
  {
    key: "earthquake_zone",
    label: "Earthquake Zone",
    group: "Geo",
    getValue: (addr) => getEarthquakeZone(addr),
  },
  {
    key: "rms_dist_to_coast",
    label: "RMS Dist. to Coast",
    group: "RMS",
    getValue: (addr) => bucketDistToCoast(addr),
    compare: compareDistToCoast,
  },
  {
    key: "hh_hurricane",
    label: "HH Hurricane Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.hurricane,
  },
  {
    key: "hh_flood",
    label: "HH Flood Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.flood,
  },
  {
    key: "hh_earthquake",
    label: "HH Earthquake Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.earthquake,
  },
  {
    key: "hh_wildfire",
    label: "HH Wildfire Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.wildfire,
  },
  {
    key: "hh_wind",
    label: "HH Wind Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.wind,
  },
  {
    key: "hh_hail",
    label: "HH Hail Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.peril_scores?.hail,
  },
  {
    key: "hh_fire_protection",
    label: "HH Fire Protection Grade",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr) =>
      addr.__extras.integrationData?.hazard_hub?.protection
        ?.fire_protection_grade,
  },
];
